import {
  type DomainCommandContext,
  DomainFault,
  type DomainModule,
  type DomainReadContext,
  type JsonObject,
  paginate,
} from '../../contracts';
import type { Command } from '../../service';
import {
  type Entity,
  type Evidence,
  type Kind,
  type Role,
  seedUuid,
} from '../../types';
import { taskResults } from '../analysis';
import { resolveEvidence } from '../recording';
import {
  type CaseHistory,
  type CaseMeasure,
  type CorrectionKind,
  conclusionLabels,
  type ReferenceImpact,
  type ReviewConclusion,
  type ReviewEvent,
  type ReviewRound,
} from './types';

export * from './types';

const managers: Role[] = ['campusManager', 'groupOperator', 'regionManager'];
const reviewers: Role[] = [...managers, 'reviewer'];
const workers: Role[] = [...reviewers, 'teacher'];
const conclusions = Object.keys(conclusionLabels) as ReviewConclusion[];
export const reviewStatus = (row: Entity) =>
  row.fields?.duplicateOfUuid ? 'merged' : row.status;
export const reviewLabels: Record<string, string> = {
  unassigned: '待分派',
  pendingReview: '待复核',
  needsInfo: '待补充',
  confirmed: '已确认',
  excluded: '已排除',
  closed: '已关闭',
  inProgress: '处理中',
  pendingAcceptance: '待验收',
  reopened: '已重开',
  merged: '已合并',
  submitted: '已提交',
  reviewing: '核实中',
  accepted: '已接受',
  rejected: '已驳回',
  draft: '草稿',
  pending: '待完成',
  completed: '已完成',
};
export function arrayField<T>(row: Entity, key: string): T[] {
  try {
    const value: unknown = JSON.parse(row.fields?.[key] || '[]');
    if (!Array.isArray(value)) throw new Error();
    return value as T[];
  } catch {
    throw new DomainFault(
      'VALIDATION',
      '历史数据格式异常，请保留数据并联系维护人员',
    );
  }
}
export const roundsFor = (row: Entity) =>
  arrayField<ReviewRound>(row, 'reviewRounds');
export const measuresFor = (row: Entity) =>
  arrayField<CaseMeasure>(row, 'caseMeasures');
export const historyFor = (row: Entity) =>
  arrayField<CaseHistory>(row, 'caseHistory');
export const impactsFor = (row: Entity) =>
  arrayField<ReferenceImpact>(row, 'referenceImpacts');
const need = (value: string | undefined, label: string) => {
  if (!value?.trim()) throw new DomainFault('VALIDATION', `请填写${label}`);
  return value.trim();
};
function get(ctx: DomainReadContext, uuid: string | undefined, kind?: Kind) {
  const row = ctx.get(uuid);
  if (!row || (kind && row.kind !== kind))
    throw new DomainFault('FORBIDDEN', '对象不存在或当前范围不可访问');
  return row;
}
function state(row: Entity, states: string[]) {
  if (!states.includes(row.status) || row.fields?.duplicateOfUuid)
    throw new DomainFault('INVALID_TRANSITION', '状态已变化，请重新预览');
}
function preview(ctx: DomainCommandContext, input: Command) {
  const time = Number(input.fields?.previewAt);
  if (!Number.isFinite(time) || Math.abs(Date.now() - time) > 900000)
    throw new DomainFault('CONFLICT', '预览已过期，请重新预览');
  if (
    input.fields?.contextKey !==
    `${ctx.context.role}:${ctx.context.campus}:${ctx.context.viewMemberUuid}`
  )
    throw new DomainFault('FORBIDDEN', '业务视角已变化，请重新预览');
  if (input.fields?.sourceVersions) {
    let versions: Record<string, number>;
    try {
      const value: unknown = JSON.parse(input.fields.sourceVersions);
      if (!value || typeof value !== 'object' || Array.isArray(value))
        throw new Error();
      versions = value as Record<string, number>;
    } catch {
      throw new DomainFault('VALIDATION', '预览版本信息无效，请重新预览');
    }
    if (
      Object.entries(versions).some(
        ([uuid, revision]) =>
          !Number.isInteger(revision) || ctx.get(uuid)?.revision !== revision,
      )
    )
      throw new DomainFault('CONFLICT', '证据来源修订已变化，请重新预览');
  }
  if (input.uuid) {
    const row = get(ctx, input.uuid);
    if (
      input.meta?.expectedRevision === undefined ||
      input.meta.expectedRevision !== row.revision
    )
      throw new DomainFault('CONFLICT', '来源版本已变化，请重新预览');
  }
}
function member(
  ctx: DomainReadContext,
  uuid: string | undefined,
  campus: string,
) {
  const target = ctx
    .members()
    .find(
      (m) =>
        m.memberUuid === uuid && m.campus === campus && m.status === 'active',
    );
  if (!target)
    throw new DomainFault('VALIDATION', '负责人必须是来源校区可分派的在职人员');
  return target.memberUuid;
}
function owner(ctx: DomainReadContext, row: Entity) {
  if (
    !managers.includes(ctx.context.role) &&
    row.owner !== ctx.context.viewMemberUuid
  )
    throw new DomainFault('FORBIDDEN', '仅负责人或所辖管理人员可以处理');
}
function future(ctx: DomainReadContext, value: string | undefined) {
  if (
    !value ||
    !Number.isFinite(Date.parse(value)) ||
    Date.parse(value) <= Date.parse(ctx.now)
  )
    throw new DomainFault('VALIDATION', '期限须晚于当前业务时间');
  return new Date(value).toISOString();
}
function set(row: Entity, key: string, value: unknown) {
  row.fields = { ...row.fields, [key]: JSON.stringify(value) };
}
function event(
  ctx: DomainCommandContext,
  row: Entity,
  type: ReviewEvent['type'],
) {
  const value: ReviewEvent = {
    uuid: crypto.randomUUID(),
    type,
    sourceUuid: row.uuid,
    at: ctx.now,
    actorMemberUuid: ctx.identity.memberUuid,
    sourceRevision: row.revision,
  };
  ctx.setData({
    ...ctx.data(),
    events: [
      ...(Array.isArray(ctx.data().events)
        ? (ctx.data().events as JsonObject[])
        : []),
      JSON.parse(JSON.stringify(value)) as JsonObject,
    ],
  });
}
function auditHistory(
  ctx: DomainCommandContext,
  row: Entity,
  action: string,
  note: string,
  before: string,
  previousDueAt?: string,
) {
  const value: CaseHistory = {
    uuid: crypto.randomUUID(),
    action,
    note,
    at: ctx.now,
    actorMemberUuid: ctx.identity.memberUuid,
    viewMemberUuid: ctx.context.viewMemberUuid,
    previousStatus: before,
    nextStatus: row.status,
    previousDueAt,
    nextDueAt: row.dueAt,
    result: row.result,
  };
  set(row, 'caseHistory', [...historyFor(row), value]);
}
function appendTask(
  ctx: DomainCommandContext,
  row: Entity,
  title: string,
  type: string,
  ownerUuid = row.owner,
  dueAt = row.dueAt,
  required = true,
) {
  const task = ctx.append('task', title, {
    status: 'pending',
    sourceUuid: row.uuid,
    campus: row.campus,
    memberUuid: row.memberUuid,
    owner: ownerUuid || ctx.context.viewMemberUuid,
    dueAt,
    fields: { taskType: type, required: String(required) },
  });
  ctx.notify(row, `已安排行动：${title}`);
  return task;
}
function finishTasks(ctx: DomainCommandContext, row: Entity, type?: string) {
  for (const task of ctx
    .list('task')
    .filter(
      (t) =>
        t.sourceUuid === row.uuid &&
        (!type || t.fields?.taskType === type) &&
        t.status !== 'completed',
    )) {
    task.status = 'completed';
    task.note = '来源对象已完成人工处理';
    ctx.touch(task);
  }
}
export function evidenceRefsFor(row: Entity): Evidence[] {
  const all = [
    ...(row.evidence ? [row.evidence] : []),
    ...arrayField<Evidence>(row, 'evidenceRefs'),
  ];
  return all.filter(
    (e, i) =>
      all.findIndex(
        (other) =>
          other.recordingUuid === e.recordingUuid &&
          other.transcriptVersionUuid === e.transcriptVersionUuid &&
          other.startMs === e.startMs &&
          other.endMs === e.endMs,
      ) === i,
  );
}
export function readableEvidence(
  ctx: Pick<DomainReadContext, 'get'>,
  row: Entity,
) {
  return evidenceRefsFor(row).map((ref) => resolveEvidence(ctx, ref));
}
export function blockerReasons(
  ctx: Pick<DomainReadContext, 'get' | 'list' | 'now'>,
  row: Entity,
): string[] {
  const reasons: string[] = [];
  if (!row.owner) reasons.push('未分派负责人');
  if (!row.dueAt) reasons.push('缺少期限');
  else if (
    row.status !== 'closed' &&
    Date.parse(row.dueAt) < Date.parse(ctx.now)
  )
    reasons.push('已超期');
  const required = arrayField<string>(row, 'necessaryTaskUuids');
  const tasks = required.map((uuid) => ctx.get(uuid));
  if (tasks.some((task) => !task)) reasons.push('必要任务不可访问，请核对权限');
  const incomplete = tasks.filter((t) => t && t.status !== 'completed').length;
  if (incomplete) reasons.push(`${incomplete}项必要任务未完成`);
  if (row.fields?.sourceChanged) reasons.push('来源版本变化，需核实影响');
  if (row.status === 'pendingAcceptance') reasons.push('等待人工验收');
  return reasons;
}
export function analysisCandidateFor(ctx: DomainReadContext, row: Entity) {
  if (!row.fields?.analysisCandidateUuid) return undefined;
  const task = get(ctx, row.fields.analysisTaskUuid || row.sourceUuid, 'job');
  const candidate = taskResults(ctx, task)
    .flatMap((result) => result.suggestions)
    .find((value) => value.uuid === row.fields?.analysisCandidateUuid);
  if (!candidate)
    throw new DomainFault('CONFLICT', '分析候选来源不可用，请核对冻结版本');
  resolveEvidence(ctx, candidate.evidence);
  return candidate;
}
export function duplicateCandidates(
  ctx: Pick<DomainReadContext, 'list'>,
  row: Entity,
) {
  return ctx
    .list('clue')
    .filter(
      (e) =>
        e.uuid !== row.uuid &&
        !e.fields?.duplicateOfUuid &&
        e.status !== 'closed' &&
        evidenceRefsFor(e).some((ref) =>
          evidenceRefsFor(row).some(
            (other) =>
              ref.recordingUuid === other.recordingUuid &&
              ref.transcriptVersionUuid === other.transcriptVersionUuid &&
              ref.startMs < other.endMs &&
              ref.endMs > other.startMs,
          ),
        ),
    );
}
export function referenceImpacts(
  ctx: Pick<DomainReadContext, 'list'>,
  source: Entity,
) {
  return (
    [
      'job',
      'clue',
      'case',
      'plan',
      'knowledge',
      'feedback',
      'followUp',
    ] as Kind[]
  )
    .flatMap((kind) => ctx.list(kind))
    .filter(
      (row) =>
        row.uuid !== source.uuid &&
        (row.sourceUuid === source.uuid ||
          evidenceRefsFor(row).some((e) => e.recordingUuid === source.uuid) ||
          (row.kind === 'job' && row.sourceUuid === source.uuid)),
    );
}
function assign(ctx: DomainCommandContext, input: Command) {
  const row = get(ctx, input.uuid);
  if (!['clue', 'case'].includes(row.kind))
    throw new DomainFault('VALIDATION', '对象不支持分派');
  state(
    row,
    row.kind === 'clue'
      ? ['unassigned', 'pendingReview', 'needsInfo']
      : ['unassigned', 'inProgress', 'reopened'],
  );
  const assigned = member(ctx, input.memberUuid, row.campus);
  const due = future(ctx, input.dueAt);
  const before = row.status;
  row.owner = assigned;
  row.dueAt = due;
  row.status = row.kind === 'clue' ? 'pendingReview' : 'inProgress';
  const current = ctx
    .list('task')
    .find(
      (t) =>
        t.sourceUuid === row.uuid &&
        t.fields?.taskType === 'review.assignment' &&
        t.status !== 'completed',
    );
  if (current) {
    current.owner = assigned;
    current.dueAt = due;
    ctx.touch(current);
  } else {
    const task = appendTask(
      ctx,
      row,
      `处理：${row.title}`,
      'review.assignment',
    );
    if (row.kind === 'case')
      set(row, 'necessaryTaskUuids', [
        ...arrayField<string>(row, 'necessaryTaskUuids'),
        task.uuid,
      ]);
  }
  auditHistory(ctx, row, 'assign', need(input.note, '分派说明'), before);
  ctx.touch(row);
  return row;
}
function submitReview(ctx: DomainCommandContext, input: Command) {
  const row = get(ctx, input.uuid, 'clue');
  state(row, ['pendingReview']);
  owner(ctx, row);
  need(row.owner, '负责人');
  const conclusion = input.conclusion as ReviewConclusion;
  if (!conclusions.includes(conclusion))
    throw new DomainFault('VALIDATION', '请选择有效结论');
  const opinion = need(input.note, '复核意见');
  const scope =
    conclusion === 'partial'
      ? need(input.fields?.confirmedScope, '成立范围')
      : input.fields?.confirmedScope || '';
  const refs = readableEvidence(ctx, row);
  if (!refs.length) throw new DomainFault('VALIDATION', '缺少可读取的版本证据');
  const rounds = roundsFor(row);
  const last = rounds.at(-1);
  if (
    last &&
    (last.reviewerMemberUuid === ctx.context.viewMemberUuid ||
      last.actorMemberUuid === ctx.identity.memberUuid)
  )
    throw new DomainFault(
      'FORBIDDEN',
      '二次复核必须由不同复核人和真实操作者提交',
    );
  const round: ReviewRound = {
    uuid: crypto.randomUUID(),
    round: rounds.length + 1,
    sourceClueUuid: row.uuid,
    reviewerMemberUuid: ctx.context.viewMemberUuid,
    actorMemberUuid: ctx.identity.memberUuid,
    conclusion,
    opinion,
    confirmedScope: scope,
    evidenceRefs: refs.map((e) => ({ ...e, quote: '' })),
    submittedAt: ctx.now,
    supersedesRoundUuid: last?.uuid,
  };
  set(row, 'reviewRounds', [...rounds, round]);
  row.conclusion = conclusionLabels[conclusion];
  row.note = opinion;
  row.status =
    conclusion === 'needsInfo'
      ? 'needsInfo'
      : conclusion === 'excluded'
        ? 'excluded'
        : 'confirmed';
  finishTasks(ctx, row, 'review.assignment');
  ctx.touch(row);
  ctx.notify(row, `人工复核已提交：${row.title}`);
  event(ctx, row, 'review.submitted');
  return row;
}
function merge(ctx: DomainCommandContext, input: Command) {
  const row = get(ctx, input.uuid, 'clue');
  const other = get(ctx, input.fields?.otherUuid, 'clue');
  if (row.uuid === other.uuid || row.campus !== other.campus)
    throw new DomainFault('VALIDATION', '请选择同校区的不同线索');
  state(row, [
    'unassigned',
    'pendingReview',
    'needsInfo',
    'confirmed',
    'excluded',
  ]);
  state(other, [
    'unassigned',
    'pendingReview',
    'needsInfo',
    'confirmed',
    'excluded',
  ]);
  if (other.revision !== Number(input.fields?.otherRevision))
    throw new DomainFault('CONFLICT', '合并来源版本已变化');
  if (
    ctx
      .list('case')
      .some((c) => [row.uuid, other.uuid].includes(c.sourceUuid || ''))
  )
    throw new DomainFault(
      'CONFLICT',
      '已有关联事项，需先独立核实，不可直接合并',
    );
  const refs = [
    ...readableEvidence(ctx, row),
    ...readableEvidence(ctx, other),
  ].map((e) => ({ ...e, quote: '' }));
  set(row, 'evidenceRefs', refs);
  set(row, 'reviewSourceUuids', [...new Set(refs.map((e) => e.recordingUuid))]);
  set(row, 'reviewRounds', [...roundsFor(row), ...roundsFor(other)]);
  set(row, 'mergedClueUuids', [
    ...arrayField<string>(row, 'mergedClueUuids'),
    other.uuid,
  ]);
  row.fields = { ...row.fields, mergeReason: need(input.note, '合并依据') };
  const before = other.status;
  other.fields = { ...other.fields, duplicateOfUuid: row.uuid };
  other.status = 'excluded';
  finishTasks(ctx, other);
  auditHistory(ctx, other, 'merged', input.note || '', before);
  // All historical opinions survive; merged sources require a fresh review before a formal case.
  row.status = row.owner ? 'pendingReview' : 'unassigned';
  row.conclusion = '';
  finishTasks(ctx, row);
  if (row.owner)
    appendTask(ctx, row, `合并后重新复核：${row.title}`, 'review.assignment');
  ctx.touch(row);
  ctx.touch(other);
  ctx.notify(row, '线索已合并，全部来源与历史保留');
  return row;
}
function createCase(ctx: DomainCommandContext, input: Command) {
  const clue = get(ctx, input.uuid, 'clue');
  state(clue, ['confirmed']);
  const last = roundsFor(clue).at(-1);
  if (!last || !['confirmed', 'partial'].includes(last.conclusion))
    throw new DomainFault(
      'INVALID_TRANSITION',
      '须先有成立或部分成立的人工复核',
    );
  const existing = ctx.list('case').find((c) => c.sourceUuid === clue.uuid);
  if (existing) return existing;
  const refs = readableEvidence(ctx, clue);
  const assigned = member(ctx, input.memberUuid, clue.campus);
  const due = future(ctx, input.dueAt);
  const value = ctx.append('case', need(input.title, '事项标题'), {
    sourceUuid: clue.uuid,
    campus: clue.campus,
    memberUuid: clue.memberUuid,
    owner: assigned,
    dueAt: due,
    status: 'inProgress',
    note: need(input.note, '事实与处理目标'),
    evidence: refs[0],
    fields: {
      reviewRoundUuid: last.uuid,
      evidenceRefs: JSON.stringify(refs.map((e) => ({ ...e, quote: '' }))),
      reviewSourceUuids: JSON.stringify(refs.map((e) => e.recordingUuid)),
      originalDueAt: due,
    },
  });
  const task = appendTask(
    ctx,
    value,
    `处理事项：${value.title}`,
    'review.assignment',
  );
  set(value, 'necessaryTaskUuids', [task.uuid]);
  clue.fields = { ...clue.fields, caseUuid: value.uuid };
  ctx.touch(clue);
  event(ctx, value, 'case.created');
  return value;
}
function caseCommand(ctx: DomainCommandContext, input: Command) {
  const row = get(ctx, input.uuid, 'case');
  const action = input.action.split('.').at(-1)!;
  const before = row.status;
  const note = need(
    input.note,
    action === 'submitResult' ? '处理结果' : '操作依据',
  );
  if (
    action !== 'accept' &&
    action !== 'reject' &&
    action !== 'reopen' &&
    action !== 'completeTask'
  )
    owner(ctx, row);
  switch (action) {
    case 'measure': {
      state(row, ['inProgress', 'reopened']);
      const assigned = member(ctx, input.memberUuid, row.campus);
      const due = future(ctx, input.dueAt);
      const task = appendTask(
        ctx,
        row,
        need(input.title, '措施标题'),
        'review.measure',
        assigned,
        due,
        input.fields?.required !== 'false',
      );
      const measure: CaseMeasure = {
        uuid: crypto.randomUUID(),
        taskUuid: task.uuid,
        title: task.title,
        ownerMemberUuid: assigned,
        dueAt: due,
        required: input.fields?.required !== 'false',
      };
      set(row, 'caseMeasures', [...measuresFor(row), measure]);
      if (measure.required)
        set(row, 'necessaryTaskUuids', [
          ...arrayField<string>(row, 'necessaryTaskUuids'),
          task.uuid,
        ]);
      break;
    }
    case 'completeTask': {
      state(row, ['inProgress', 'reopened']);
      const task = get(ctx, input.fields?.taskUuid, 'task');
      if (task.sourceUuid !== row.uuid)
        throw new DomainFault('VALIDATION', '任务不属于此事项');
      owner(ctx, task);
      state(task, ['pending', 'inProgress']);
      const dependencies = arrayField<string>(task, 'dependsOn');
      if (
        dependencies.some((id) => get(ctx, id, 'task').status !== 'completed')
      )
        throw new DomainFault('INVALID_TRANSITION', '前置任务未完成');
      task.status = 'completed';
      task.note = note;
      task.endAt = ctx.now;
      ctx.touch(task);
      set(
        row,
        'caseMeasures',
        measuresFor(row).map((m) =>
          m.taskUuid === task.uuid
            ? { ...m, result: note, completedAt: ctx.now }
            : m,
        ),
      );
      break;
    }
    case 'submitResult':
      state(row, ['inProgress', 'reopened']);
      row.result = note;
      row.status = 'pendingAcceptance';
      row.fields = {
        ...row.fields,
        submittedByMemberUuid: ctx.identity.memberUuid,
        submittedAt: ctx.now,
      };
      break;
    case 'accept': {
      state(row, ['pendingAcceptance']);
      need(row.result, '处理结果');
      need(row.owner, '负责人');
      for (const uuid of arrayField<string>(row, 'necessaryTaskUuids'))
        if (get(ctx, uuid, 'task').status !== 'completed')
          throw new DomainFault(
            'INVALID_TRANSITION',
            '必要任务未完成，不能验收关闭',
          );
      if (row.fields?.sourceChanged && input.fields?.ackImpact !== 'true')
        throw new DomainFault('CONFLICT', '来源已有更正，请先核实影响');
      if (input.fields?.ackImpact === 'true') {
        set(
          row,
          'referenceImpacts',
          impactsFor(row).map((impact) =>
            impact.status === 'pending'
              ? {
                  ...impact,
                  status: 'acknowledged',
                  acknowledgedAt: ctx.now,
                  acknowledgedByMemberUuid: ctx.identity.memberUuid,
                }
              : impact,
          ),
        );
        row.fields = {
          ...row.fields,
          sourceChanged: '',
          impactAcknowledgementNote: note,
        };
        finishTasks(ctx, row, 'review.impact');
      }
      row.status = 'closed';
      row.endAt = ctx.now;
      row.fields = {
        ...row.fields,
        acceptedAt: ctx.now,
        acceptedByMemberUuid: ctx.identity.memberUuid,
      };
      const clue = get(ctx, row.sourceUuid, 'clue');
      if (clue.status === 'confirmed') {
        clue.status = 'closed';
        ctx.touch(clue);
      }
      break;
    }
    case 'reject':
      state(row, ['pendingAcceptance']);
      row.status = 'inProgress';
      ctx.notify(row, '验收已退回，请补充处理结果');
      break;
    case 'reopen': {
      state(row, ['closed']);
      row.status = 'reopened';
      row.endAt = undefined;
      const clue = get(ctx, row.sourceUuid, 'clue');
      if (clue.status === 'closed') {
        clue.status = 'confirmed';
        ctx.touch(clue);
      }
      const task = appendTask(
        ctx,
        row,
        `重开跟进：${row.title}`,
        'review.assignment',
      );
      set(row, 'necessaryTaskUuids', [
        ...arrayField<string>(row, 'necessaryTaskUuids'),
        task.uuid,
      ]);
      break;
    }
    case 'extend': {
      state(row, ['inProgress', 'reopened', 'pendingAcceptance']);
      const next = future(ctx, input.dueAt);
      if (row.dueAt && Date.parse(next) <= Date.parse(row.dueAt))
        throw new DomainFault('VALIDATION', '新期限必须晚于原期限');
      const previous = row.dueAt;
      row.fields = {
        ...row.fields,
        originalDueAt: row.fields?.originalDueAt || previous || '',
      };
      row.dueAt = next;
      for (const task of ctx
        .list('task')
        .filter(
          (entry) =>
            entry.sourceUuid === row.uuid &&
            entry.status !== 'completed' &&
            entry.fields?.taskType === 'review.assignment',
        )) {
        task.fields = {
          ...task.fields,
          originalDueAt: task.fields?.originalDueAt || task.dueAt || '',
        };
        task.dueAt = next;
        ctx.touch(task);
      }
      auditHistory(ctx, row, action, note, before, previous);
      ctx.notify(row, `事项延期至${next}；原期限保留`);
      ctx.touch(row);
      event(ctx, row, 'case.extended');
      return row;
    }
    case 'escalate':
      state(row, ['inProgress', 'reopened', 'pendingAcceptance']);
      row.fields = {
        ...row.fields,
        escalatedAt: ctx.now,
        escalationReason: note,
      };
      ctx.notify(row, `事项升级：${row.title}`);
      break;
    case 'remind':
      state(row, ['inProgress', 'reopened', 'pendingAcceptance']);
      ctx.notify(row, `事项催办：${note}`);
      break;
    default:
      throw new DomainFault('VALIDATION', '未知事项操作');
  }
  auditHistory(ctx, row, action, note, before);
  ctx.touch(row);
  if (action === 'accept') event(ctx, row, 'case.accepted');
  if (action === 'reopen') event(ctx, row, 'case.reopened');
  return row;
}
function correctionPropose(ctx: DomainCommandContext, input: Command) {
  const target = get(ctx, input.uuid);
  const kind = input.fields?.correctionKind as CorrectionKind;
  if (!['transcript', 'conclusion', 'association', 'speaker'].includes(kind))
    throw new DomainFault('VALIDATION', '更正类型无效');
  if (
    kind === 'conclusion' ? target.kind !== 'clue' : target.kind !== 'recording'
  )
    throw new DomainFault('VALIDATION', '目标类型与更正类型不一致');
  if (input.fields?.previousCorrectionUuid) {
    const previous = get(
      ctx,
      input.fields.previousCorrectionUuid,
      'correction',
    );
    if (
      !['accepted', 'rejected'].includes(previous.status) ||
      previous.fields?.targetUuid !== target.uuid
    )
      throw new DomainFault('CONFLICT', '再次核实须关联同一对象已裁决的申请');
  }
  const proposed = need(input.fields?.proposedValue, '建议值');
  const reason = need(input.note, '更正依据');
  let original = '';
  let versionUuid = '';
  let segmentUuid = '';
  if (kind === 'transcript') {
    const version = target.versions?.at(-1);
    const segment = version?.segments.find(
      (s) => s.uuid === input.fields?.segmentUuid,
    );
    if (!version || !segment)
      throw new DomainFault('VALIDATION', '请选择当前版本的原文片段');
    original = segment.text;
    versionUuid = version.uuid;
    segmentUuid = segment.uuid;
  } else if (kind === 'conclusion') {
    if (!conclusions.includes(proposed as ReviewConclusion))
      throw new DomainFault('VALIDATION', '建议结论无效');
    original = target.conclusion || '';
  } else if (kind === 'association') {
    const lesson = get(ctx, proposed, 'lesson');
    if (lesson.campus !== target.campus)
      throw new DomainFault('VALIDATION', '课次校区不一致');
    original = target.fields?.lessonUuid || '';
  } else {
    const label = need(input.fields?.speaker, '说话人');
    if (
      !target.versions?.at(-1)?.segments.some((s) => s.speaker === label) ||
      !['教师', '学生', '家长', '其他', '未确认'].includes(proposed)
    )
      throw new DomainFault('VALIDATION', '说话人或角色无效');
    original =
      JSON.parse(target.fields?.speakerRoles || '{}')[label]?.role || '未确认';
  }
  const value = ctx.append(
    'correction',
    input.title?.trim() || '来源更正申请',
    {
      status: 'submitted',
      sourceUuid: target.uuid,
      campus: target.campus,
      memberUuid: target.memberUuid,
      note: reason,
      fields: {
        previousCorrectionUuid: input.fields?.previousCorrectionUuid || '',
        correctionKind: kind,
        targetUuid: target.uuid,
        targetRevision: String(target.revision),
        originalValue: original,
        proposedValue: proposed,
        versionUuid,
        segmentUuid,
        speaker: input.fields?.speaker || '',
        proposerMemberUuid: ctx.context.viewMemberUuid,
        proposerRealMemberUuid: ctx.identity.memberUuid,
        submittedAt: ctx.now,
      },
    },
  );
  ctx.notify(value, `更正申请已提交：${value.title}`);
  return value;
}
function correctionCommand(ctx: DomainCommandContext, input: Command) {
  const row = get(ctx, input.uuid, 'correction');
  const action = input.action.split('.').at(-1)!;
  const before = row.status;
  const note = need(input.note, '核实依据');
  if (action === 'assignCorrection') {
    state(row, ['submitted', 'reviewing', 'needsInfo']);
    row.owner = member(ctx, input.memberUuid, row.campus);
    row.status = 'reviewing';
    const tasks = ctx
      .list('task')
      .filter((t) => t.sourceUuid === row.uuid && t.status !== 'completed');
    if (!tasks.length)
      appendTask(ctx, row, `核实更正：${row.title}`, 'review.correction');
    else
      for (const t of tasks) {
        t.owner = row.owner;
        ctx.touch(t);
      }
  } else if (action === 'supplementCorrection') {
    state(row, ['needsInfo']);
    row.status = 'reviewing';
    row.fields = { ...row.fields, supplement: note };
  } else {
    state(row, ['reviewing']);
    owner(ctx, row);
    need(row.owner, '核实人');
    if (
      row.fields?.proposerRealMemberUuid === ctx.identity.memberUuid ||
      row.fields?.proposerMemberUuid === ctx.context.viewMemberUuid
    )
      throw new DomainFault('FORBIDDEN', '申请人不能裁决自己的更正');
    if (action === 'requestInfo') {
      row.status = 'needsInfo';
    } else if (action === 'rejectCorrection') {
      row.status = 'rejected';
      finishTasks(ctx, row);
    } else if (action === 'acceptCorrection') {
      const target = get(ctx, row.fields?.targetUuid);
      if (target.revision !== Number(row.fields?.targetRevision))
        throw new DomainFault(
          'CONFLICT',
          '原对象已变化，请重新提出更正，不能覆盖新版本',
        );
      const kind = row.fields?.correctionKind;
      const proposed = need(row.fields?.proposedValue, '建议值');
      if (kind === 'transcript') {
        const old = target.versions?.at(-1);
        if (!old || old.uuid !== row.fields?.versionUuid)
          throw new DomainFault('CONFLICT', '转写输入版本已变化');
        target.versions?.push({
          uuid: crypto.randomUUID(),
          createdAt: ctx.now,
          segments: old.segments.map((s) => ({
            ...s,
            uuid: crypto.randomUUID(),
            text: s.uuid === row.fields?.segmentUuid ? proposed : s.text,
          })),
        });
      } else if (kind === 'conclusion') {
        const last = roundsFor(target).at(-1);
        const round: ReviewRound = {
          uuid: crypto.randomUUID(),
          round: roundsFor(target).length + 1,
          sourceClueUuid: target.uuid,
          reviewerMemberUuid: ctx.context.viewMemberUuid,
          actorMemberUuid: ctx.identity.memberUuid,
          conclusion: proposed as ReviewConclusion,
          opinion: note,
          confirmedScope: row.note,
          evidenceRefs: readableEvidence(ctx, target).map((e) => ({
            ...e,
            quote: '',
          })),
          submittedAt: ctx.now,
          supersedesRoundUuid: last?.uuid,
        };
        set(target, 'reviewRounds', [...roundsFor(target), round]);
        target.conclusion = conclusionLabels[round.conclusion];
        target.status =
          round.conclusion === 'excluded'
            ? 'excluded'
            : round.conclusion === 'needsInfo'
              ? 'needsInfo'
              : 'confirmed';
      } else if (kind === 'association') {
        const lesson = get(ctx, proposed, 'lesson');
        if (lesson.campus !== target.campus)
          throw new DomainFault('VALIDATION', '课次校区已变化');
        set(target, 'associationHistory', [
          ...arrayField<object>(target, 'associationHistory'),
          {
            previous: target.fields?.lessonUuid,
            selected: lesson.uuid,
            reason: note,
            at: ctx.now,
            correctionUuid: row.uuid,
          },
        ]);
        target.fields = {
          ...target.fields,
          lessonUuid: lesson.uuid,
          linkStatus: 'confirmed',
        };
        ctx.touch(lesson);
      } else if (kind === 'speaker') {
        target.fields = {
          ...target.fields,
          speakerRoles: JSON.stringify({
            ...JSON.parse(target.fields?.speakerRoles || '{}'),
            [row.fields?.speaker || '']: {
              role: proposed,
              confirmedAt: ctx.now,
              confirmedByMemberUuid: ctx.identity.memberUuid,
            },
          }),
        };
      } else
        throw new DomainFault(
          'VALIDATION',
          '此历史申请缺少受支持的更正类型，请另建申请',
        );
      const impacts = referenceImpacts(ctx, target).map((e) => ({
        uuid: crypto.randomUUID(),
        sourceUuid: target.uuid,
        sourceVersionUuid: row.fields?.versionUuid,
        targetUuid: e.uuid,
        targetKind: e.kind,
        correctionUuid: row.uuid,
        status: 'pending' as const,
        at: ctx.now,
      }));
      for (const impact of impacts) {
        const related = get(ctx, impact.targetUuid);
        related.fields = {
          ...related.fields,
          sourceChanged: '来源更正已接受，请对照旧版本核实影响',
        };
        set(related, 'referenceImpacts', [...impactsFor(related), impact]);
        ctx.touch(related);
        appendTask(
          ctx,
          related,
          `核实更正影响：${related.title}`,
          'review.impact',
          related.owner,
          undefined,
          false,
        );
      }
      set(row, 'referenceImpacts', impacts);
      row.status = 'accepted';
      row.fields = {
        ...row.fields,
        newVersionUuid: target.versions?.at(-1)?.uuid || '',
        decidedAt: ctx.now,
        decisionNote: note,
      };
      ctx.touch(target);
      finishTasks(ctx, row);
    } else throw new DomainFault('VALIDATION', '更正操作无效');
  }
  auditHistory(ctx, row, action, note, before);
  ctx.touch(row);
  if (['accepted', 'rejected'].includes(row.status))
    event(ctx, row, 'correction.decided');
  return row;
}
function additionalClueCommand(ctx: DomainCommandContext, input: Command) {
  const row = get(ctx, input.uuid, 'clue');
  const action = input.action.split('.').at(-1)!;
  const note = need(input.note, action === 'draft' ? '草稿意见' : '操作依据');
  owner(ctx, row);
  if (action === 'mark') {
    const priority = input.fields?.priority;
    if (!['high', 'normal', 'low'].includes(priority || ''))
      throw new DomainFault('VALIDATION', '优先标记无效');
    row.fields = {
      ...row.fields,
      manualPriority: priority || 'normal',
      priorityReason: note,
    };
    auditHistory(ctx, row, 'mark', note, row.status);
  } else if (action === 'draft') {
    state(row, ['pendingReview', 'needsInfo']);
    row.fields = {
      ...row.fields,
      reviewDraft: JSON.stringify({
        opinion: note,
        conclusion: input.conclusion || '',
        confirmedScope: input.fields?.confirmedScope || '',
        author: ctx.context.viewMemberUuid,
        at: ctx.now,
      }),
    };
  } else if (action === 'supplement') {
    state(row, ['needsInfo']);
    row.status = 'pendingReview';
    set(row, 'supplements', [
      ...arrayField<object>(row, 'supplements'),
      {
        uuid: crypto.randomUUID(),
        note,
        at: ctx.now,
        actorMemberUuid: ctx.identity.memberUuid,
      },
    ]);
    appendTask(ctx, row, `复核补充材料：${row.title}`, 'review.assignment');
  } else if (action === 'rereview') {
    state(row, ['confirmed', 'excluded', 'closed']);
    row.status = 'pendingReview';
    row.fields = { ...row.fields, rereviewReason: note };
    appendTask(ctx, row, `二次复核：${row.title}`, 'review.assignment');
  } else if (action === 'resolveWithoutCase') {
    state(row, ['confirmed']);
    if (
      ctx
        .list('case')
        .some((c) => c.sourceUuid === row.uuid && c.status !== 'closed')
    )
      throw new DomainFault(
        'INVALID_TRANSITION',
        '关联事项未验收，不能结束线索',
      );
    row.status = 'closed';
    row.fields = { ...row.fields, noCaseReason: note };
    finishTasks(ctx, row);
  } else if (action === 'addEvidence') {
    state(row, ['pendingReview', 'needsInfo']);
    if (!input.evidence) throw new DomainFault('VALIDATION', '请选择补充证据');
    const e = resolveEvidence(ctx, input.evidence);
    const source = get(ctx, e.recordingUuid, 'recording');
    if (source.campus !== row.campus)
      throw new DomainFault('FORBIDDEN', '补充证据须在同一授权校区');
    set(row, 'evidenceRefs', [...evidenceRefsFor(row), { ...e, quote: '' }]);
    set(row, 'reviewSourceUuids', [
      ...new Set(evidenceRefsFor(row).map((ref) => ref.recordingUuid)),
    ]);
    set(row, 'supplements', [
      ...arrayField<object>(row, 'supplements'),
      {
        uuid: crypto.randomUUID(),
        note,
        at: ctx.now,
        actorMemberUuid: ctx.identity.memberUuid,
      },
    ]);
  } else throw new DomainFault('VALIDATION', '未知线索操作');
  ctx.touch(row);
  return row;
}
function migration(
  state: Parameters<NonNullable<DomainModule['migrations']>[number]['up']>[0],
) {
  state.domainData ||= {};
  state.domainData.review ||= { events: [] };
  // Preserve user data; only populate the domain cohort when no clues exist yet.
  if (!state.entities.some((e) => e.kind === 'clue')) {
    let ordinal = 0;
    for (const campus of [
      ...new Set(
        state.entities
          .filter((e) => e.kind === 'recording')
          .map((e) => e.campus),
      ),
    ]) {
      const sources = state.entities
        .filter(
          (e) =>
            e.kind === 'recording' && e.campus === campus && e.versions?.length,
        )
        .slice(0, 3);
      const person = state.entities.find(
        (e) =>
          e.kind === 'teacher' && e.campus === campus && e.status === 'active',
      );
      sources.forEach((recording, i) => {
        const version = recording.versions![0];
        const segment = version.segments[4] || version.segments[0];
        if (!segment) return;
        const evidence = {
          recordingUuid: recording.uuid,
          transcriptVersionUuid: version.uuid,
          startMs: segment.startMs,
          endMs: segment.endMs,
          quote: segment.text,
        };
        const uuid = seedUuid(960000 + ordinal++);
        const ownerUuid = person?.memberUuid || recording.memberUuid;
        const status = ['unassigned', 'pendingReview', 'confirmed'][i];
        const row: Entity = {
          uuid,
          kind: 'clue',
          title: `${['片段待核实', '片段待复核', '后续安排已确认'][i]} · ${recording.title}`,
          campus,
          memberUuid: recording.memberUuid,
          status,
          createdAt: state.now,
          updatedAt: state.now,
          revision: 1,
          note: '虚构演示线索，需以原始片段为依据。',
          evidence,
          owner: i ? ownerUuid : undefined,
          dueAt: new Date(Date.parse(state.now) + 86400000).toISOString(),
          fields: {
            priority: i === 0 ? 'high' : 'normal',
            ruleBasis: '预置片段人工核查示例；不代表模型判断',
            reviewRounds: '[]',
          },
        };
        if (i === 2) {
          row.conclusion = '成立';
          set(row, 'reviewRounds', [
            {
              uuid: seedUuid(962000 + ordinal),
              round: 1,
              sourceClueUuid: uuid,
              reviewerMemberUuid: ownerUuid,
              conclusion: 'confirmed',
              opinion:
                '已对照虚构原文确认存在后续安排，实际履行情况需另行核实。',
              confirmedScope: '原文中的后续安排',
              evidenceRefs: [{ ...evidence, quote: '' }],
              submittedAt: state.now,
            } satisfies ReviewRound,
          ]);
        }
        state.entities.push(row);
        if (
          i === 0 &&
          !state.entities.some(
            (entry) => entry.kind === 'correction' && entry.campus === campus,
          )
        ) {
          const original = version.segments[0];
          const proposer =
            state.entities.find(
              (entry) =>
                entry.kind === 'teacher' &&
                entry.campus === campus &&
                entry.memberUuid !== ownerUuid,
            )?.memberUuid || seedUuid(50011);
          state.entities.push({
            uuid: seedUuid(969000 + ordinal),
            kind: 'correction',
            title: '虚构台词用语校正申请',
            campus,
            memberUuid: recording.memberUuid,
            status: 'submitted',
            createdAt: state.now,
            updatedAt: state.now,
            revision: 1,
            note: '请核对原片段的表达，模拟演示更正裁决与版本追溯。',
            sourceUuid: recording.uuid,
            fields: {
              correctionKind: 'transcript',
              targetUuid: recording.uuid,
              targetRevision: String(recording.revision),
              originalValue: original.text,
              proposedValue: `${original.text}（模拟核实补充：以原始上下文为准。）`,
              versionUuid: version.uuid,
              segmentUuid: original.uuid,
              proposerMemberUuid: proposer,
              submittedAt: state.now,
            },
          });
        }
        if (i === 1) {
          state.entities.push({
            ...row,
            uuid: seedUuid(963000 + ordinal),
            title: `同源待核实片段 · ${campus}`,
            status: 'unassigned',
            owner: undefined,
            fields: { ...row.fields, reviewRounds: '[]' },
          });
        }
        if (i === 2) {
          const caseUuid = seedUuid(964000 + ordinal);
          const taskUuid = seedUuid(965000 + ordinal);
          const due = new Date(
            Date.parse(state.now) + 2 * 86400000,
          ).toISOString();
          const item: Entity = {
            ...row,
            uuid: caseUuid,
            kind: 'case',
            title: '后续安排核实与反馈',
            sourceUuid: row.uuid,
            status: 'inProgress',
            owner: ownerUuid,
            dueAt: due,
            conclusion: undefined,
            fields: {
              necessaryTaskUuids: JSON.stringify([taskUuid]),
              caseMeasures: '[]',
              caseHistory: '[]',
              originalDueAt: due,
            },
          };
          state.entities.push(item, {
            ...item,
            uuid: taskUuid,
            kind: 'task',
            title: '核实处理情况并登记可验收结果',
            sourceUuid: caseUuid,
            status: 'pending',
            fields: { taskType: 'review.assignment', required: 'true' },
          });
          row.fields = { ...row.fields, caseUuid };
        }
      });
    }
  }
  for (const row of state.entities.filter((e) =>
    ['clue', 'case', 'correction'].includes(e.kind),
  )) {
    row.fields ||= {};
    if (row.kind === 'clue' && !row.fields.reviewRounds) {
      const legacy = Object.entries(row.fields)
        .filter(([key]) => key.startsWith('review-'))
        .map(([, v], i) => {
          const old = JSON.parse(v) as {
            conclusion: string;
            note: string;
            reviewer: string;
            at: string;
          };
          return {
            uuid: seedUuid(968000 + state.entities.indexOf(row) * 20 + i),
            round: i + 1,
            sourceClueUuid: row.uuid,
            reviewerMemberUuid: old.reviewer,
            conclusion: (Object.entries(conclusionLabels).find(
              ([, label]) => label === old.conclusion,
            )?.[0] || 'needsInfo') as ReviewConclusion,
            opinion: old.note,
            confirmedScope: '',
            evidenceRefs: row.evidence ? [{ ...row.evidence, quote: '' }] : [],
            submittedAt: old.at,
          };
        });
      set(row, 'reviewRounds', legacy);
    }
    if (row.kind === 'case' && !row.fields.necessaryTaskUuids)
      set(
        row,
        'necessaryTaskUuids',
        state.entities
          .filter(
            (e) =>
              e.kind === 'task' &&
              e.sourceUuid === row.uuid &&
              e.fields?.required !== 'false',
          )
          .map((e) => e.uuid),
      );
    if (row.kind === 'correction' && row.status === 'pendingReview')
      row.status = 'submitted';
  }
}
function command(
  roles: Role[],
  kind: Kind,
  execute: (ctx: DomainCommandContext, input: Command) => Entity,
): DomainModule['commands'][string] {
  return {
    roles,
    kind,
    execute(ctx, input) {
      preview(ctx, input);
      return execute(ctx, input);
    },
  };
}
export const reviewDomain: DomainModule = {
  namespace: 'review',
  version: 1,
  resource: 'clue',
  pages: [
    {
      id: 'V01',
      path: '/review/clue',
      resource: 'clue',
      parentPath: '/review/clue',
      returnTo: '/review/clue',
    },
    {
      id: 'V02',
      path: '/review/clue/:clueUuid',
      resource: 'clue',
      parentPath: '/review/clue',
      returnTo: '/review/clue',
    },
    {
      id: 'V03',
      path: '/case',
      resource: 'case',
      parentPath: '/review/clue',
      returnTo: '/case',
    },
    {
      id: 'V04',
      path: '/case/:caseUuid',
      resource: 'case',
      parentPath: '/review/clue',
      returnTo: '/case',
    },
    {
      id: 'V05',
      path: '/review/correction',
      resource: 'correction',
      parentPath: '/review/clue',
      returnTo: '/review/correction',
    },
  ],
  migrations: [{ version: 1, up: migration }],
  commands: {
    assignClue: command(reviewers, 'clue', assign),
    assignCase: command(managers, 'case', assign),
    submit: command(reviewers, 'clue', submitReview),
    mark: command(reviewers, 'clue', additionalClueCommand),
    merge: command(managers, 'clue', merge),
    createCase: command(reviewers, 'case', createCase),
    ...Object.fromEntries(
      [
        'draft',
        'supplement',
        'rereview',
        'resolveWithoutCase',
        'addEvidence',
      ].map((name) => [
        name,
        command(
          name === 'draft' || name === 'supplement' || name === 'addEvidence'
            ? workers
            : reviewers,
          'clue',
          additionalClueCommand,
        ),
      ]),
    ),
    ...Object.fromEntries(
      [
        'measure',
        'completeTask',
        'submitResult',
        'extend',
        'escalate',
        'remind',
        'accept',
        'reject',
        'reopen',
      ].map((name) => [
        name,
        command(
          ['accept', 'reject', 'reopen', 'extend'].includes(name)
            ? managers
            : workers,
          'case',
          caseCommand,
        ),
      ]),
    ),
    proposeCorrection: command(workers, 'correction', correctionPropose),
    ...Object.fromEntries(
      [
        'assignCorrection',
        'supplementCorrection',
        'requestInfo',
        'acceptCorrection',
        'rejectCorrection',
      ].map((name) => [
        name,
        command(
          name === 'supplementCorrection' ? workers : reviewers,
          'correction',
          correctionCommand,
        ),
      ]),
    ),
  },
  query(ctx, input) {
    const kinds: Kind[] = input.kinds?.filter((kind) =>
      ['clue', 'case', 'correction'].includes(kind),
    ) || ['clue'];
    const rows = kinds
      .flatMap((kind) => ctx.list(kind))
      .filter(
        (row) =>
          !row.fields?.duplicateOfUuid &&
          (!input.keyword || row.title.includes(input.keyword)) &&
          (!input.status || row.status === input.status) &&
          (!input.memberUuid || row.owner === input.memberUuid),
      );
    return paginate(rows, input, ctx.revision);
  },
  search(ctx) {
    return (['clue', 'case', 'correction'] as const)
      .flatMap((kind) => ctx.list(kind))
      .filter((row) => !row.fields?.duplicateOfUuid)
      .map((row) => ({
        id: row.uuid,
        entityUuid: row.uuid,
        title: row.title,
        text: row.note,
        kind: row.kind,
        memberUuid: row.memberUuid,
        occurredAt: row.createdAt,
        evidence: row.evidence,
      }));
  },
  metrics(ctx) {
    const rows = ctx.list('case');
    return [
      {
        uuid: 'review-case-closure-v1',
        scope: ctx.context.campus,
        asOf: ctx.now,
        metric: 'caseClosure',
        numerator: rows.filter((row) => row.status === 'closed').length,
        denominator: rows.length || null,
        unit: '事项',
        sourceUuids: rows.map((row) => row.uuid),
        definitionVersion: 'review-v1',
      },
    ];
  },
};
